import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import css from './CheckoutForm.css';

const InvoiceSection = ({ intl }) => {
  const { register, watch, setValue, formState: { errors } } = useFormContext();
  const invoiceType = watch('invoiceType');
  const isCompany = invoiceType === 'company';

  const validateNIP = (value) => {
    if (!isCompany) return true;
    if (!value) return intl.formatMessage({ id: 'InvoiceSection.error.taxIdRequired' });
  
    const cleanNIP = value.replace(/[\s-]/g, '').toUpperCase();
    
    const nipRegex = /^PL\d{10}$/;
    if (!nipRegex.test(cleanNIP)) {
      return intl.formatMessage({ id: 'InvoiceSection.error.taxIdInvalidFormat' });
    }
  
    const digits = cleanNIP.replace('PL', '').split('').map(Number);
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    const sum = weights.reduce((acc, weight, idx) => acc + weight * digits[idx], 0);
    const checksum = sum % 11;
  
    if (checksum === 10 || checksum !== digits[9]) {
      return intl.formatMessage({ id: 'InvoiceSection.error.taxIdChecksumInvalid' });
    }
  
    return true;
  };

  const placeholder = id => intl.formatMessage({ id });

  useEffect(() => {
    if (isCompany) {
      setValue('invoice.country', 'Polska', { shouldValidate: true });
    }
  }, [isCompany, setValue]);

  return (
    <section className={css.section}>
      <h2 className={css.heading}>
        <FormattedMessage id="InvoiceSection.title" />
      </h2>

      <div className={css.typeSwitcher}>
        <button
          type="button"
          className={classNames(css.switchBtn, !isCompany && css.switchBtnActive)}
          onClick={() => setValue('invoiceType', 'individual')}
        >
          <FormattedMessage id="InvoiceSection.individual" />
        </button>
        <button
          type="button"
          className={classNames(css.switchBtn, isCompany && css.switchBtnActive)}
          onClick={() => {
            setValue('invoiceType', 'company');
            setValue('invoice.country', 'Polska', { shouldValidate: true });
          }}
        >
          <FormattedMessage id="InvoiceSection.company" />
        </button>
      </div>

      <div className={css.invoiceFields}>
        {isCompany ? (
          <div className={css.rowWithMargin}>
            <div className={css.field}>
              <label className={css.label}>
                <FormattedMessage id="InvoiceSection.companyName" />
              </label>
              <input
                {...register("invoice.companyName", { required: isCompany })}
                placeholder={placeholder("InvoiceSection.placeholder.companyName")}
                className={errors.invoice?.companyName ? css.inputError : css.input}
              />
            </div>
            <div className={css.field}>
              <label className={css.label}>
                <FormattedMessage id="InvoiceSection.taxId" />
              </label>
              <input
                {...register("invoice.taxId", {
                  required: isCompany,
                  validate: validateNIP,
                })}
                placeholder={placeholder("InvoiceSection.placeholder.taxId")}
                className={errors.invoice?.taxId ? css.inputError : css.input}
              />
              {errors.invoice?.taxId && (
                <span className={css.fieldErrorText}>
                  {errors.invoice.taxId.message}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className={classNames(css.field, css.marginBottom)}>
            <label className={css.label}>
              <FormattedMessage id="InvoiceSection.nameOnInvoice" />
            </label>
            <input
              {...register("invoice.name", { required: !isCompany })}
              placeholder={placeholder("InvoiceSection.placeholder.name")}
              className={errors.invoice?.name ? css.inputError : css.input}
            />
          </div>
        )}

        <div className={css.rowWithMargin}>
          <div className={css.field}>
            <label className={css.label}>
              <FormattedMessage id="InvoiceSection.country" />
            </label>
            <input
              {...register("invoice.country", { required: true })}
              placeholder={placeholder("InvoiceSection.placeholder.selectCountry")}
              className={errors.invoice?.country ? css.inputError : css.input}
              type="text"
              disabled={isCompany}
            />
          </div>
          <div className={css.field}>
            <label className={css.label}>
              <FormattedMessage id="InvoiceSection.city" />
            </label>
            <input
              {...register("invoice.city", { required: true })}
              placeholder={placeholder("InvoiceSection.placeholder.city")}
              className={errors.invoice?.city ? css.inputError : css.input}
            />
          </div>
        </div>

        <div className={css.addressGrid}>
          <div className={css.field}>
            <label className={css.label}>
              <FormattedMessage id="InvoiceSection.address" />
            </label>
            <input
              {...register("invoice.address", { required: true })}
              placeholder={placeholder("InvoiceSection.placeholder.address")}
              className={errors.invoice?.address ? css.inputError : css.input}
            />
          </div>
          <div className={css.field}>
            <label className={css.label}>
              <FormattedMessage id="InvoiceSection.zip" />
            </label>
            <input
              {...register("invoice.zip", { required: true })}
              placeholder={placeholder("InvoiceSection.placeholder.zip")}
              className={errors.invoice?.zip ? css.inputError : css.input}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default injectIntl(InvoiceSection);